import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBulkDeleteWorkOrders } from '@/features/work-orders/queries';

/**
 * L16 — bulk soft-delete confirmation modal.
 *
 * Mirrors `BulkPriorityModal`: shows the affected count, surfaces
 * failed ids (signed / closed / immutable WOs the backend skipped)
 * inline rather than aborting the whole batch.
 */
export function BulkDeleteModal({
  open,
  onOpenChange,
  ids,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ids: string[];
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [result, setResult] = useState<{
    deletedIds: string[];
    failedIds: string[];
  } | null>(null);
  const mut = useBulkDeleteWorkOrders();

  const onConfirm = () => {
    setResult(null);
    mut.mutate(
      { ids },
      {
        onSuccess: (out) => {
          setResult(out);
          if (out.failedIds.length === 0) {
            onSuccess();
            onOpenChange(false);
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mut.reset();
          setResult(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('bulk.delete.title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm">
            {t('bulk.delete.description', { count: ids.length })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('bulk.delete.softNote')}
          </p>
          {result && result.failedIds.length > 0 && (
            <p
              className="text-sm text-amber-700"
              data-testid="bulk-delete-partial"
            >
              {t('bulk.delete.partial', {
                deleted: result.deletedIds.length,
                failed: result.failedIds.length,
              })}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={mut.isPending}
            data-testid="bulk-delete-confirm"
          >
            {t('bulk.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
